import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, ApiClientError } from '../services/api-client';
import { BrokerProfileManager } from './BrokerProfileManager';
import { IbkrConnectionSettings } from './IbkrConnectionSettings';
import { useTheme } from '../providers/ThemeProvider';
import type { ThemePreference } from '../theme/resolveEffectiveTheme';
import type {
  HearAboutUsChoice,
  InvestingExperienceChoice,
  PlanId,
  PrimaryGoalChoice,
  SegmentationSelection,
  UserProfile,
  UserProfileUpdateInput,
} from '@covered-calls/shared';
import { getProfile, updateProfile, recordConsent } from '../services/profile-service';
import { createPortalSession } from '../services/payments-service';
import { redirectTo } from '../services/navigation';
import { planDisplayName } from '../pages/plan-catalog';
import { PlanTierIcon } from './PlanTierIcon';
import { getPrivacyUrl } from '../services/runtime-config';

/**
 * Settings page — tabbed layout with Appearance, Exit Alert, Broker Profiles,
 * IBKR Connection, and Profile.
 *
 * Validates: Requirements 6.3
 */

type SettingsTab = 'appearance' | 'alerts' | 'brokers' | 'ibkr' | 'profile';

interface ExitThresholdResponse {
  exitAlertThreshold: number;
}

export function Settings() {
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');

  return (
    <div style={{ padding: '1rem', maxWidth: '900px' }}>
      <h1>Settings</h1>

      {/* Tab navigation */}
      <nav aria-label="Settings sections" style={{ marginTop: '1rem', marginBottom: '1.5rem' }}>
        <div role="tablist" style={{ display: 'flex', gap: '0.25rem', borderBottom: '2px solid var(--color-border)' }}>
          <TabButton
            id="tab-profile"
            label="Profile"
            isActive={activeTab === 'profile'}
            onClick={() => setActiveTab('profile')}
          />
          <TabButton
            id="tab-appearance"
            label="Appearance"
            isActive={activeTab === 'appearance'}
            onClick={() => setActiveTab('appearance')}
          />
          <TabButton
            id="tab-alerts"
            label="Exit Alerts"
            isActive={activeTab === 'alerts'}
            onClick={() => setActiveTab('alerts')}
          />
          <TabButton
            id="tab-brokers"
            label="Broker Profiles"
            isActive={activeTab === 'brokers'}
            onClick={() => setActiveTab('brokers')}
          />
          <TabButton
            id="tab-ibkr"
            label="IBKR Connection"
            isActive={activeTab === 'ibkr'}
            onClick={() => setActiveTab('ibkr')}
          />
        </div>
      </nav>

      {/* Tab panels */}
      <div role="tabpanel" aria-labelledby={`tab-${activeTab}`}>
        {activeTab === 'profile' && <ProfileSettings />}
        {activeTab === 'appearance' && <AppearanceSettings />}
        {activeTab === 'alerts' && <ExitAlertSettings />}
        {activeTab === 'brokers' && <BrokerProfileManager />}
        {activeTab === 'ibkr' && <IbkrConnectionSettings />}
      </div>
    </div>
  );
}

// ─── Tab Button ─────────────────────────────────────────────────────────────

function TabButton({ id, label, isActive, onClick }: { id: string; label: string; isActive: boolean; onClick: () => void }) {
  return (
    <button
      id={id}
      role="tab"
      aria-selected={isActive}
      onClick={onClick}
      style={{
        padding: '0.75rem 1.25rem',
        border: 'none',
        borderBottom: isActive ? '2px solid var(--color-brand)' : '2px solid transparent',
        backgroundColor: 'transparent',
        color: isActive ? 'var(--color-brand)' : 'var(--color-muted)',
        fontWeight: isActive ? 600 : 400,
        cursor: 'pointer',
        minHeight: '44px',
        marginBottom: '-2px',
        fontSize: '0.95rem',
      }}
    >
      {label}
    </button>
  );
}

// ─── Appearance Settings (theme control) ────────────────────────────────────

/**
 * Three-way theme control (Light / Dark / System) wired to the ThemeProvider.
 *
 * Uses native <fieldset>/<legend> + three <input type="radio"> so the control is
 * inherently keyboard operable (arrow-key navigation within the radio group) and
 * carries an accessible label identifying it as the theme selection (Req 3.8).
 * Selecting a value applies immediately and persists via context, with no reload
 * (Req 3.1–3.7). A persistence failure surfaces a non-blocking inline message
 * without reverting the selection (Req 7.3).
 */
function AppearanceSettings() {
  const { preference, setPreference, persistError } = useTheme();

  const options: { value: ThemePreference; label: string }[] = [
    { value: 'Light', label: 'Light' },
    { value: 'Dark', label: 'Dark' },
    { value: 'System', label: 'System' },
  ];

  return (
    <section aria-labelledby="appearance-heading">
      <h2 id="appearance-heading" style={{ marginBottom: '0.5rem' }}>Appearance</h2>
      <p style={{ color: 'var(--color-muted)', marginBottom: '1rem' }}>
        Choose how the application looks. System follows your operating system's light or dark setting
        and updates automatically when it changes.
      </p>

      <fieldset style={{ border: 'none', margin: 0, padding: 0 }}>
        <legend style={{ fontWeight: 500, marginBottom: '0.5rem' }}>Theme</legend>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {options.map(({ value, label }) => {
            const inputId = `theme-preference-${value.toLowerCase()}`;
            return (
              <label
                key={value}
                htmlFor={inputId}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', minHeight: '44px' }}
              >
                <input
                  id={inputId}
                  type="radio"
                  name="theme-preference"
                  value={value}
                  checked={preference === value}
                  onChange={() => setPreference(value)}
                />
                {label}
              </label>
            );
          })}
        </div>
      </fieldset>

      {persistError && (
        <p role="alert" style={{ marginTop: '1rem', color: 'var(--color-danger)', fontWeight: 500 }}>
          Your theme was applied but couldn't be saved. It may not persist to other devices.
        </p>
      )}
    </section>
  );
}

// ─── Exit Alert Settings (extracted from original Settings) ─────────────────

function ExitAlertSettings() {
  const [currentThreshold, setCurrentThreshold] = useState<number | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const fetchThreshold = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage('');
    try {
      const data = await apiClient.get<ExitThresholdResponse>('/settings/exit-threshold');
      setCurrentThreshold(data.exitAlertThreshold);
      setInputValue(String(data.exitAlertThreshold));
    } catch (error) {
      if (error instanceof ApiClientError) {
        setErrorMessage(`Failed to load settings: ${error.message}`);
      } else {
        setErrorMessage('Failed to load settings. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchThreshold();
  }, [fetchThreshold]);

  function validate(value: string): string | null {
    const trimmed = value.trim();
    if (trimmed === '') {
      return 'Exit alert threshold is required.';
    }
    const num = Number(trimmed);
    if (!Number.isInteger(num)) {
      return 'Exit alert threshold must be a whole number.';
    }
    if (num < 1 || num > 50) {
      return 'Exit alert threshold must be between 1 and 50.';
    }
    return null;
  }

  async function handleSave() {
    setSuccessMessage('');
    setErrorMessage('');

    const validationError = validate(inputValue);
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    const threshold = Number(inputValue.trim());

    setIsSaving(true);
    try {
      await apiClient.put('/settings/exit-threshold', { exitAlertThreshold: threshold });
      setCurrentThreshold(threshold);
      setSuccessMessage('Exit alert threshold saved successfully.');
    } catch (error) {
      if (error instanceof ApiClientError) {
        setErrorMessage(`Failed to save settings: ${error.message}`);
      } else {
        setErrorMessage('Failed to save settings. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return <p style={{ color: 'var(--color-muted)' }}>Loading exit alert settings...</p>;
  }

  return (
    <section aria-labelledby="exit-threshold-heading">
      <h2 id="exit-threshold-heading" style={{ marginBottom: '0.5rem' }}>Exit Alert Threshold</h2>
      <p style={{ color: 'var(--color-muted)', marginBottom: '1rem' }}>
        When the stock price drops more than this percentage below your breakeven price, an exit alert will be displayed.
      </p>

      {currentThreshold !== null && (
        <p style={{ marginBottom: '1rem' }}>
          Current threshold: <strong>{currentThreshold}%</strong>
        </p>
      )}

      <div style={{ marginBottom: '1rem' }}>
        <label htmlFor="exit-threshold-input" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
          Threshold (1–50%)
        </label>
        <input
          id="exit-threshold-input"
          type="number"
          min={1}
          max={50}
          step={1}
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setSuccessMessage('');
            setErrorMessage('');
          }}
          aria-describedby="threshold-help"
          style={{
            width: '100%',
            maxWidth: '120px',
            padding: '0.5rem 1rem',
            fontSize: '1rem',
            border: '1px solid var(--color-input-border)',
            borderRadius: '4px',
            minHeight: '44px',
          }}
          disabled={isSaving}
        />
        <span id="threshold-help" className="sr-only">
          Enter a whole number between 1 and 50.
        </span>
      </div>

      <button
        onClick={handleSave}
        disabled={isSaving || inputValue === String(currentThreshold)}
        style={{
          padding: '0.75rem 1.5rem',
          backgroundColor: inputValue !== String(currentThreshold) ? 'var(--color-brand)' : 'var(--color-brand-disabled)',
          color: 'var(--color-on-brand)',
          border: 'none',
          borderRadius: '4px',
          cursor: (isSaving || inputValue === String(currentThreshold)) ? 'not-allowed' : 'pointer',
          fontSize: '1rem',
          minHeight: '44px',
          opacity: isSaving ? 0.7 : 1,
          transition: 'background-color 0.2s',
        }}
      >
        {isSaving ? 'Saving...' : 'Save'}
      </button>

      {successMessage && (
        <p role="status" style={{ marginTop: '1rem', color: 'var(--color-success)', fontWeight: 500 }}>
          {successMessage}
        </p>
      )}

      {errorMessage && (
        <p role="alert" style={{ marginTop: '1rem', color: 'var(--color-danger)', fontWeight: 500 }}>
          {errorMessage}
        </p>
      )}
    </section>
  );
}

// ─── Profile Settings (Component 9 — Profile_Section) ───────────────────────


/** Max lengths for the free-text controls (Req 6.3). */
const PROFILE_DISPLAY_NAME_MAX = 100;
const PROFILE_OTHER_VALUE_MAX = 200;

/** The empty sentinel value that keeps a select at "— Select —" (field unset). */
const PROFILE_UNSET = '';

/** A single option entry for a fixed-choice <select>. */
interface ProfileChoiceOption<C extends string> {
  value: C;
  label: string;
}

/**
 * Country/region options — kept in lockstep with the OnboardingPrompt list so
 * the two surfaces stay consistent (Req 6.3). `OTHER` is a catch-all.
 */
const PROFILE_COUNTRIES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'AU', label: 'Australia' },
  { value: 'US', label: 'United States' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'CA', label: 'Canada' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'IE', label: 'Ireland' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'SG', label: 'Singapore' },
  { value: 'HK', label: 'Hong Kong' },
  { value: 'JP', label: 'Japan' },
  { value: 'IN', label: 'India' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'OTHER', label: 'Other' },
];

const PROFILE_HEAR_ABOUT_US_OPTIONS: ReadonlyArray<ProfileChoiceOption<HearAboutUsChoice>> = [
  { value: 'SEARCH', label: 'Search engine' },
  { value: 'SOCIAL_MEDIA', label: 'Social media' },
  { value: 'FRIEND', label: 'Friend or colleague' },
  { value: 'PODCAST', label: 'Podcast' },
  { value: 'OTHER', label: 'Other' },
];

const PROFILE_INVESTING_EXPERIENCE_OPTIONS: ReadonlyArray<
  ProfileChoiceOption<InvestingExperienceChoice>
> = [
  { value: 'BEGINNER', label: 'Beginner' },
  { value: 'INTERMEDIATE', label: 'Intermediate' },
  { value: 'ADVANCED', label: 'Advanced' },
];

const PROFILE_PRIMARY_GOAL_OPTIONS: ReadonlyArray<ProfileChoiceOption<PrimaryGoalChoice>> = [
  { value: 'INCOME', label: 'Income' },
  { value: 'GROWTH', label: 'Growth' },
  { value: 'HEDGING', label: 'Hedging' },
  { value: 'LEARNING', label: 'Learning' },
  { value: 'OTHER', label: 'Other' },
];

/**
 * Build a `SegmentationSelection` from a raw select value and its optional
 * free-text. Returns `undefined` when the field is left unset, so the caller
 * can omit it from the update input entirely. An `OTHER` choice carries the
 * trimmed `otherValue`; a blank free-text is dropped. Mirrors the prompt's
 * `buildSelection` helper (Req 6.3).
 */
function buildProfileSelection<C extends string>(
  choice: string,
  otherValue: string
): SegmentationSelection<C> | undefined {
  if (choice === PROFILE_UNSET) {
    return undefined;
  }
  if (choice === 'OTHER') {
    const trimmed = otherValue.trim();
    return { choice: choice as C, otherValue: trimmed.length > 0 ? trimmed : undefined };
  }
  return { choice: choice as C };
}

/** Common inline styles for form controls, using theme tokens only (Req 8.4). */
const profileControlStyle = {
  width: '100%',
  maxWidth: '360px',
  padding: '0.5rem',
  border: '1px solid var(--color-input-border)',
  borderRadius: '4px',
  backgroundColor: 'var(--color-input-bg)',
  color: 'var(--color-text)',
  fontSize: '1rem',
  minHeight: '44px',
} as const;

const profileLabelStyle = {
  display: 'block',
  marginBottom: '0.25rem',
  fontWeight: 500,
} as const;

const profileFieldStyle = {
  marginBottom: '1rem',
} as const;

/**
 * Profile_Section (Component 9). Loads the profile on mount and displays every
 * Profile_Field as empty-and-editable (never omitting absent fields — Req 6.1,
 * 6.2), editing each with the same control types as the OnboardingPrompt incl.
 * the conditional "Other" free-text (Req 6.3, 6.4). Save issues `PUT /profile`
 * (omitting `dismiss` so a Settings edit never re-triggers the prompt or reverts
 * a terminal state — Req 6.13); on failure it retains the entered values and
 * surfaces a `role="alert"` error (Req 6.5, 6.6, 6.7). Marketing consent shows
 * the current state (Req 6.8) with a single symmetric grant/withdraw control
 * (Req 6.9) issuing `recordConsent(granted, 'SETTINGS')` (Req 6.10, 6.11),
 * grouped with the Privacy_Information link (Req 6.12). Accessibility per Req 8.
 *
 * Validates: Requirements 6.1, 6.2, 6.3, 6.4, 6.5, 6.6, 6.7, 6.8, 6.9, 6.10,
 * 6.11, 6.12, 6.13, 8.1, 8.2, 8.3, 8.4, 8.5, 8.7.
 */
function ProfileSettings() {
  // Privacy information URL, resolved from runtime config (per-env
  // marketing/privacy URL) with a built-in fallback (Path 2 domain migration).
  const PRIVACY_URL = getPrivacyUrl();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // Editable field state.
  const [displayName, setDisplayName] = useState('');
  const [countryRegion, setCountryRegion] = useState<string>(PROFILE_UNSET);
  const [hearAboutUs, setHearAboutUs] = useState<string>(PROFILE_UNSET);
  const [hearAboutUsOther, setHearAboutUsOther] = useState('');
  const [investingExperience, setInvestingExperience] = useState<string>(PROFILE_UNSET);
  const [primaryGoal, setPrimaryGoal] = useState<string>(PROFILE_UNSET);
  const [primaryGoalOther, setPrimaryGoalOther] = useState('');

  // Consent state (current granted flag from the loaded profile — Req 6.8).
  const [consentGranted, setConsentGranted] = useState(false);
  const [consentSaving, setConsentSaving] = useState(false);
  const [consentError, setConsentError] = useState('');

  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState('');
  const [saveError, setSaveError] = useState('');

  // Manage-subscription (Stripe Customer Portal) state (Req 8.3).
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState('');

  // Current plan id, sourced from the optional `plan` block on the profile
  // response. Non-blocking: null when absent / not yet loaded (treated as trial).
  const [currentPlanId, setCurrentPlanId] = useState<PlanId | null>(null);

  const headingRef = useRef<HTMLHeadingElement>(null);
  const saveErrorRef = useRef<HTMLParagraphElement>(null);

  /** Seed the editable state from a loaded profile. */
  const seedFromProfile = useCallback((profile: UserProfile) => {
    setDisplayName(profile.displayName ?? '');
    setCountryRegion(profile.countryRegion ?? PROFILE_UNSET);
    setHearAboutUs(profile.hearAboutUs?.choice ?? PROFILE_UNSET);
    setHearAboutUsOther(profile.hearAboutUs?.otherValue ?? '');
    setInvestingExperience(profile.investingExperience?.choice ?? PROFILE_UNSET);
    setPrimaryGoal(profile.primaryGoal?.choice ?? PROFILE_UNSET);
    setPrimaryGoalOther(profile.primaryGoal?.otherValue ?? '');
    setConsentGranted(profile.consent?.granted ?? false);
  }, []);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    setLoadError('');
    getProfile()
      .then((profile) => {
        // getProfile always resolves (NOT_STARTED default when absent).
        if (active) {
          // Capture the current plan id from the optional plan block on the
          // ProfileWithPlan response before seeding the editable UserProfile fields.
          setCurrentPlanId(profile.plan?.planId ?? null);
          seedFromProfile(profile);
        }
      })
      .catch((error) => {
        if (!active) {
          return;
        }
        if (error instanceof ApiClientError) {
          setLoadError(`Failed to load your profile: ${error.message}`);
        } else {
          setLoadError('Failed to load your profile. Please try again.');
        }
      })
      .finally(() => {
        if (active) {
          setIsLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [seedFromProfile]);

  // Place initial focus on the section heading when it mounts (Req 8.5).
  useEffect(() => {
    if (!isLoading) {
      headingRef.current?.focus();
    }
  }, [isLoading]);

  /**
   * Assemble a `UserProfileUpdateInput` from the non-empty fields only. Fields
   * left unset are omitted so they are not overwritten. `dismiss` is
   * intentionally never sent from Settings (Req 6.13).
   */
  function buildInput(): UserProfileUpdateInput {
    const input: UserProfileUpdateInput = {};
    const trimmedName = displayName.trim();
    if (trimmedName.length > 0) {
      input.displayName = trimmedName;
    }
    if (countryRegion !== PROFILE_UNSET) {
      input.countryRegion = countryRegion;
    }
    const hear = buildProfileSelection<HearAboutUsChoice>(hearAboutUs, hearAboutUsOther);
    if (hear) {
      input.hearAboutUs = hear;
    }
    const experience = buildProfileSelection<InvestingExperienceChoice>(investingExperience, '');
    if (experience) {
      input.investingExperience = experience;
    }
    const goal = buildProfileSelection<PrimaryGoalChoice>(primaryGoal, primaryGoalOther);
    if (goal) {
      input.primaryGoal = goal;
    }
    return input;
  }

  async function handleSave() {
    if (isSaving) {
      return;
    }
    setSaveSuccess('');
    setSaveError('');
    setIsSaving(true);
    try {
      const updated = await updateProfile(buildInput());
      // Reflect the persisted profile back into the fields (values retained).
      seedFromProfile(updated);
      setSaveSuccess('Profile saved.');
    } catch {
      // Retain the entered values (do not clear) and surface the error (Req 6.7).
      setSaveError("Couldn't save your changes. Please try again.");
      requestAnimationFrame(() => saveErrorRef.current?.focus());
    } finally {
      setIsSaving(false);
    }
  }

  async function handleToggleConsent() {
    if (consentSaving) {
      return;
    }
    const next = !consentGranted;
    setConsentError('');
    setConsentSaving(true);
    try {
      const updated = await recordConsent(next, 'SETTINGS');
      setConsentGranted(updated.consent?.granted ?? next);
    } catch {
      // Leave the prior state unchanged on failure.
      setConsentError("Couldn't update your marketing preference. Please try again.");
    } finally {
      setConsentSaving(false);
    }
  }

  /**
   * Open the Stripe Customer Portal (Req 8.3). Requests a portal session and, on
   * success, redirects the browser to the returned hosted portal URL. On failure
   * it surfaces an inline `role="alert"` error near the control and does not
   * redirect. A loading/disabled state mirrors the existing save/consent pattern.
   */
  /**
   * Navigate to the Plans / Pricing screen so a user can proactively choose or
   * change a plan without first hitting a limit. Uses react-router's in-app
   * navigation (the Plans route lives at `/plans`).
   */
  function handleViewPlans() {
    navigate('/plans');
  }

  async function handleManageSubscription() {
    if (portalLoading) {
      return;
    }
    setPortalError('');
    setPortalLoading(true);
    try {
      const { url } = await createPortalSession();
      redirectTo(url);
    } catch {
      setPortalError("Couldn't open the subscription portal. Please try again.");
      setPortalLoading(false);
    }
  }

  if (isLoading) {
    return <p style={{ color: 'var(--color-muted)' }}>Loading your profile...</p>;
  }

  if (loadError) {
    return (
      <section aria-labelledby="profile-heading">
        <h2 id="profile-heading" tabIndex={-1} ref={headingRef} style={{ marginBottom: '0.5rem' }}>
          Profile
        </h2>
        <p role="alert" style={{ color: 'var(--color-danger)', fontWeight: 500 }}>
          {loadError}
        </p>
      </section>
    );
  }

  const showHearAboutUsOther = hearAboutUs === 'OTHER';
  const showPrimaryGoalOther = primaryGoal === 'OTHER';

  return (
    <section aria-labelledby="profile-heading">
      <h2
        id="profile-heading"
        ref={headingRef}
        tabIndex={-1}
        className="focus-target"
        style={{ marginBottom: '0.5rem' }}
      >
        Profile
      </h2>
      <p style={{ color: 'var(--color-muted)', marginBottom: '1rem' }}>
        These details are optional. Fill in as much or as little as you like — you can update them
        any time.
      </p>

      {/* Display name (Req 6.3) */}
      <div style={profileFieldStyle}>
        <label htmlFor="profile-display-name" style={profileLabelStyle}>
          Display name
        </label>
        <input
          id="profile-display-name"
          type="text"
          value={displayName}
          onChange={(e) => {
            setDisplayName(e.target.value);
            setSaveSuccess('');
            setSaveError('');
          }}
          maxLength={PROFILE_DISPLAY_NAME_MAX}
          style={profileControlStyle}
          disabled={isSaving}
        />
      </div>

      {/* Country / region (Req 6.3) */}
      <div style={profileFieldStyle}>
        <label htmlFor="profile-country-region" style={profileLabelStyle}>
          Country / region
        </label>
        <select
          id="profile-country-region"
          value={countryRegion}
          onChange={(e) => {
            setCountryRegion(e.target.value);
            setSaveSuccess('');
            setSaveError('');
          }}
          style={profileControlStyle}
          disabled={isSaving}
        >
          <option value={PROFILE_UNSET}>— Select —</option>
          {PROFILE_COUNTRIES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </div>

      {/* How did you hear about us? (Req 6.3, 6.4) */}
      <div style={profileFieldStyle}>
        <label htmlFor="profile-hear-about-us" style={profileLabelStyle}>
          How did you hear about us?
        </label>
        <select
          id="profile-hear-about-us"
          value={hearAboutUs}
          onChange={(e) => {
            setHearAboutUs(e.target.value);
            setSaveSuccess('');
            setSaveError('');
          }}
          style={profileControlStyle}
          disabled={isSaving}
        >
          <option value={PROFILE_UNSET}>— Select —</option>
          {PROFILE_HEAR_ABOUT_US_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {showHearAboutUsOther && (
          <div style={{ marginTop: '0.5rem' }}>
            <label htmlFor="profile-hear-about-us-other" style={profileLabelStyle}>
              Tell us how
            </label>
            <input
              id="profile-hear-about-us-other"
              type="text"
              value={hearAboutUsOther}
              onChange={(e) => {
                setHearAboutUsOther(e.target.value);
                setSaveSuccess('');
                setSaveError('');
              }}
              maxLength={PROFILE_OTHER_VALUE_MAX}
              style={profileControlStyle}
              disabled={isSaving}
            />
          </div>
        )}
      </div>

      {/* Investing experience (Req 6.3 — no Other) */}
      <div style={profileFieldStyle}>
        <label htmlFor="profile-investing-experience" style={profileLabelStyle}>
          Investing experience
        </label>
        <select
          id="profile-investing-experience"
          value={investingExperience}
          onChange={(e) => {
            setInvestingExperience(e.target.value);
            setSaveSuccess('');
            setSaveError('');
          }}
          style={profileControlStyle}
          disabled={isSaving}
        >
          <option value={PROFILE_UNSET}>— Select —</option>
          {PROFILE_INVESTING_EXPERIENCE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {/* Primary goal (Req 6.3, 6.4) */}
      <div style={profileFieldStyle}>
        <label htmlFor="profile-primary-goal" style={profileLabelStyle}>
          Primary goal
        </label>
        <select
          id="profile-primary-goal"
          value={primaryGoal}
          onChange={(e) => {
            setPrimaryGoal(e.target.value);
            setSaveSuccess('');
            setSaveError('');
          }}
          style={profileControlStyle}
          disabled={isSaving}
        >
          <option value={PROFILE_UNSET}>— Select —</option>
          {PROFILE_PRIMARY_GOAL_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {showPrimaryGoalOther && (
          <div style={{ marginTop: '0.5rem' }}>
            <label htmlFor="profile-primary-goal-other" style={profileLabelStyle}>
              Tell us your goal
            </label>
            <input
              id="profile-primary-goal-other"
              type="text"
              value={primaryGoalOther}
              onChange={(e) => {
                setPrimaryGoalOther(e.target.value);
                setSaveSuccess('');
                setSaveError('');
              }}
              maxLength={PROFILE_OTHER_VALUE_MAX}
              style={profileControlStyle}
              disabled={isSaving}
            />
          </div>
        )}
      </div>

      <button
        onClick={handleSave}
        disabled={isSaving}
        style={{
          padding: '0.75rem 1.5rem',
          backgroundColor: 'var(--color-brand)',
          color: 'var(--color-on-brand)',
          border: 'none',
          borderRadius: '4px',
          cursor: isSaving ? 'not-allowed' : 'pointer',
          fontSize: '1rem',
          minHeight: '44px',
          opacity: isSaving ? 0.7 : 1,
          transition: 'background-color 0.2s',
        }}
      >
        {isSaving ? 'Saving...' : 'Save'}
      </button>

      {saveSuccess && (
        <p role="status" style={{ marginTop: '1rem', color: 'var(--color-success)', fontWeight: 500 }}>
          {saveSuccess}
        </p>
      )}

      {saveError && (
        <p
          ref={saveErrorRef}
          role="alert"
          tabIndex={-1}
          className="focus-target"
          style={{ marginTop: '1rem', color: 'var(--color-danger)', fontWeight: 500 }}
        >
          {saveError}
        </p>
      )}

      {/* Marketing consent grouped with the privacy link (Req 6.8-6.12) */}
      <fieldset
        style={{
          border: '1px solid var(--color-border)',
          borderRadius: '4px',
          padding: '0.75rem',
          marginTop: '1.5rem',
        }}
      >
        <legend style={{ padding: '0 0.25rem', color: 'var(--color-muted)' }}>Marketing</legend>

        <p style={{ marginTop: 0, marginBottom: '0.75rem' }}>
          Marketing emails: <strong>{consentGranted ? 'On' : 'Off'}</strong>
        </p>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
          <input
            id="profile-marketing-consent"
            type="checkbox"
            checked={consentGranted}
            onChange={handleToggleConsent}
            disabled={consentSaving}
            style={{ marginTop: '0.2rem', minWidth: '20px', minHeight: '20px' }}
          />
          <label htmlFor="profile-marketing-consent">
            I&apos;d like to receive product news and marketing emails.
          </label>
        </div>

        {consentError && (
          <p role="alert" style={{ margin: '0.5rem 0 0', color: 'var(--color-danger)', fontWeight: 500 }}>
            {consentError}
          </p>
        )}

        <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem' }}>
          <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
            Privacy information
          </a>
        </p>
      </fieldset>

      {/* Subscription management via the Stripe Customer Portal (Req 8.3, 12.2, 12.3) */}
      <fieldset
        style={{
          border: '1px solid var(--color-border)',
          borderRadius: '4px',
          padding: '0.75rem',
          marginTop: '1.5rem',
        }}
      >
        <legend style={{ padding: '0 0.25rem', color: 'var(--color-muted)' }}>Subscription</legend>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.75rem' }}>
          <PlanTierIcon planId={currentPlanId ?? 'trial'} size={28} />
          <span>Current plan: <strong>{planDisplayName(currentPlanId ?? 'trial')}</strong></span>
        </div>

        <p style={{ marginTop: 0, marginBottom: '0.75rem' }}>
          Choose or change your plan, manage your payment method and cancel your subscription.
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
          <button
            type="button"
            className="upgrade-cta"
            onClick={handleViewPlans}
            style={{
              marginTop: 0,
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              minHeight: '44px',
              cursor: 'pointer',
            }}
          >
            View plans
          </button>

          <button
            type="button"
            className="manage-subscription"
            onClick={handleManageSubscription}
          disabled={portalLoading}
          style={{
            padding: '0.75rem 1.5rem',
            fontSize: '1rem',
            minHeight: '44px',
            cursor: portalLoading ? 'not-allowed' : 'pointer',
            opacity: portalLoading ? 0.7 : 1,
          }}
        >
            {portalLoading ? 'Opening…' : 'Manage subscription'}
          </button>
        </div>

        {portalError && (
          <p role="alert" style={{ margin: '0.5rem 0 0', color: 'var(--color-danger)', fontWeight: 500 }}>
            {portalError}
          </p>
        )}
      </fieldset>
    </section>
  );
}
